import { useState } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { saveExportedFile } from '../../contractor-payments/saveFile';
import { formLabels } from '../types';
import type { DashboardDetail, OutsideDocument, TaxPaymentScope } from '../dashboardTypes';
import { DASH, formatBytes, formatDay, formatInstant, formatMoney } from '../format';
import { methodLabel } from '../drawer/statusCopy';
import { describeFailure } from '../drawer/failures';
import { taxPaymentsGateway } from '../index';

/**
 * View Payment Record (Figma S19 to S22): read-only. Zero, one or several files. A file is opened only through the authorized download endpoint (tenant checked on
 * the server); the browser never receives a storage key or path, only the file itself, which is handed to the browser's download.
 */
export function ViewPaymentRecordDialog({ detail, scope, companyName, onClose }: { detail: DashboardDetail; scope: TaxPaymentScope; companyName: string | null; onClose: () => void }) {
  const record = detail.outsideRecord;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { row } = detail;

  async function download(doc: OutsideDocument) {
    setBusyId(doc.id);
    setError(null);
    try {
      const file = await taxPaymentsGateway.downloadDocument(row.id, doc.id, scope);
      saveExportedFile({ blob: file.blob, fileName: file.fileName || doc.displayName });
    } catch (err) {
      setError(describeFailure(err, 'The file couldn’t be opened. Try again.').message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <ModalDialog title="Outside Tax Payment Record" size="md" className="tpd-modal" onClose={onClose}
      footer={<button type="button" className="jr-btn jr-btn--primary" data-autofocus onClick={onClose}>Close</button>}>
      <p className="tpd-muted">{companyName ? `${companyName} · ` : ''}{row.agency} — {row.jurisdiction} · {formLabels[row.formCode] ?? row.formCode}</p>
      {!record && <p data-testid="record-missing">There is no outside payment record for this payment.</p>}
      {record && (
        <>
          {record.status === 'Voided' && (
            <div className="tpd-banner tpd-banner--danger" role="note" data-testid="record-voided">
              <strong>Voided.</strong> {record.voidReason ?? ''} {record.voidedAtUtc ? `(${formatInstant(record.voidedAtUtc)})` : ''} The record is kept for the audit trail.
            </div>
          )}
          <dl className="tpd-record-grid">
            <div><dt>Payment Date</dt><dd>{formatDay(record.paymentDate)}</dd></div>
            <div><dt>Amount</dt><dd>{formatMoney(record.amount)}</dd></div>
            <div><dt>Payment Method</dt><dd>{methodLabel(record.method)}</dd></div>
            <div><dt>Agency Confirmation / Reference No.</dt><dd>{record.referenceNumber ?? DASH}</dd></div>
            <div><dt>Recorded by</dt><dd>{record.recordedByRole}</dd></div>
            <div><dt>Recorded</dt><dd>{formatInstant(record.recordedAtUtc)}</dd></div>
          </dl>
          <h4 className="tpd-subhead">Supporting Documents ({record.documents.length})</h4>
          {record.documents.length === 0 ? (
            <p className="tpd-muted" data-testid="record-no-files">No supporting documents were attached.</p>
          ) : (
            <ul className="tpd-files" aria-label="Supporting documents">
              {record.documents.map((doc) => (
                <li key={doc.id} className="tpd-file">
                  <span className="tpd-file__text">
                    <span className="tpd-file__name">{doc.displayName}</span>
                    <span className="tpd-file__meta">{doc.contentType} · {formatBytes(doc.sizeBytes)}</span>
                  </span>
                  <button type="button" className="tpd-link" disabled={busyId !== null} onClick={() => void download(doc)} aria-label={`Download ${doc.displayName}`}>
                    {busyId === doc.id ? 'Opening…' : 'Download'}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="jr-error" role="alert">{error}</p>}
          <div className="tpd-record-notes">
            <h4 className="tpd-subhead">Notes</h4>
            <p>{record.notes ?? <span className="tpd-muted">{DASH}</span>}</p>
          </div>
        </>
      )}
    </ModalDialog>
  );
}
